import React from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, IconButton, Typography } from '@mui/material';
import { t } from 'i18next';
import { HiOutlineTrash as DeleteIcon } from "react-icons/hi";

import { MuiStyledInput, MuiStyledSelect } from '../muiControls';
import RenderIf from '../../renderIf';

import useFeesTable from './useFeesTable';
import useCurrencyDisplay from '../../../utils/useCurrencyDisplay';

/**A React component, leveraging MUI, for handling the Fees and Tuitions associated with different Grade Levels.
 * 
 * @param {int} schoolId The School ID from the PCG Scholar database that identifies the currently viewed School. Found in User Settings.
 * @param {boolean} disabled Control the entire tables interactivity externally.
 * @returns 
 */
const FeesTable = ({
    schoolId,
    saving,
    ...restOfProps
}) => {
    const {
        //States
        isInitialized,
        isDoingAjax,
        sets,
        chosenGrades,
        showDeleteConfirmation,
        confirmationMessageTitle,
        confirmationMessage,
        //Handlers
        initialize,
        addGroup,
        confirmDeleteGroup,
        onDeleteConfirm,
        onDeleteCancel,
        addFee,
        updateFee,
        validateFeeDescription,
        validateFeeAmount,
        saveRow,
        deleteFee,
        handleGradeChange,
        handleGradeSave,
        showErrors,
        //Styles
        groupBoxSx,
        groupTitleSx,
        selectSx,
        feeGroupSx,
        feeDescriptionSx,
        feeAmountSx,
        feeDeleteButtonSx,
        feeGroupButtonsSx,
        addGroupButtonSx,
    } = useFeesTable();

    const { translateCurrency } = useCurrencyDisplay();

    React.useEffect(() => {
        initialize?.(schoolId);
    }, []);

    React.useEffect(() => {
        if(saving) showErrors?.();
    }, [saving]);

    return (
        <Box>
            <Dialog
                open={showDeleteConfirmation}
            >
                <DialogTitle>{confirmationMessageTitle}</DialogTitle>
                <DialogContent>
                    <Box>
                        <Typography>
                            {confirmationMessage}
                        </Typography>
                    </Box>

                    <Box>
                        <Button
                            onClick={() => onDeleteCancel?.()}
                        >
                            Cancel
                        </Button>

                        <Button
                            onClick={() => onDeleteConfirm?.()}
                            variant="contained"
                        >
                            Confirm
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
            {sets?.map?.((set, i) => {
                return <Box
                    sx={groupBoxSx}
                >
                    <Typography
                        sx={groupTitleSx}
                    >
                        Grade Level Group {set.GroupNumber}
                    </Typography>

                    <MuiStyledSelect
                        id={`grade-level-${i}`}
                        label="Grades"
                        multiple
                        value={set.selectedLevels}
                        onChange={(event) => handleGradeChange(event.target.value, set.GradeLevelGroupID)}
                        onClose={() => handleGradeSave(schoolId, set.GroupNumber)}
                        error={set?.showLevelError && set?.levelError}
                        errorMessage={set?.showLevelError && set?.levelErrorMessage}
                        disabled={isDoingAjax || saving}
                        sx={selectSx}
                    >
                        {Object.keys(set.levels).map((key) => {
                            return {
                                label: t(`GradeLevel.Levels.${key}`),
                                id: key,
                                disabled: chosenGrades.includes(key) && !set.selectedLevels.includes(key),
                            }
                        })}
                    </MuiStyledSelect>

                    {set?.fees?.map((fee, j) => {
                        return <Box
                            sx={feeGroupSx}
                            onBlur={(event) => saveRow(event, set.GradeLevelGroupID, fee.GroupFeeID)}
                        >
                            <MuiStyledInput
                                label={j === 0 ? 'Tuition and Fees Description:' : undefined} 
                                key={`fee-name-${i}-${j}`}
                                textFieldSxObj={feeDescriptionSx}
                                disabled={j === 0 || isDoingAjax || saving}
                                error={fee?.showNameError && fee?.nameError}
                                errorMessage={fee?.showNameError && fee?.nameErrorMessage ? fee.nameErrorMessage : undefined}
                                value={fee?.FeeDescription}
                                onChange={(event) => updateFee(set.GradeLevelGroupID, fee.GroupFeeID, 'FeeDescription', event.target.value)}
                                onBlur={(event) => validateFeeDescription(set.GradeLevelGroupID, fee.GroupFeeID, event)}

                            />

                            <MuiStyledInput
                                label={j === 0 ? 'Amount:' : undefined}
                                key={`fee-amount-${i}-${j}`}
                                textFieldSxObj={feeAmountSx}
                                disabled={isDoingAjax || saving}
                                error={fee?.showAmountError && fee?.amountError}
                                errorMessage={fee?.showAmountError && fee?.amountErrorMessage ? fee.amountErrorMessage : undefined}
                                value={translateCurrency(fee.Amount)?.replace('$', '')}
                                onChange={(event) => updateFee(set.GradeLevelGroupID, fee.GroupFeeID, 'Amount', event.target.value)}
                                onBlur={(event) => validateFeeAmount(set.GradeLevelGroupID, fee.GroupFeeID, event)}
                                InputProps={{
                                    startAdornment: "$"
                                }}
                            />

                            <RenderIf isTrue={j > 0}>
                                <IconButton
                                    color='error'
                                    sx={feeDeleteButtonSx}
                                    onClick={() => deleteFee(fee.GroupFeeID, schoolId)}
                                    disabled={!isInitialized || isDoingAjax || saving}
                                >
                                    <DeleteIcon/>
                                </IconButton>
                            </RenderIf>
                        </Box>;
                    })}

                    <Box
                        sx={feeGroupButtonsSx}
                    >
                        <Button
                            onClick={() => addFee(set?.GradeLevelGroupID, schoolId)}
                            disabled={!isInitialized || isDoingAjax || saving}
                        >
                            +Add new fee
                        </Button>

                        <Button
                            variant='contained'
                            color='error'
                            // onClick={() => deleteGroup(schoolId, set?.GroupNumber)}
                            onClick={() => confirmDeleteGroup(schoolId, set?.GroupNumber)}
                            disabled={!isInitialized || isDoingAjax || saving}
                        >
                            Delete group
                        </Button>
                    </Box>
                </Box>
            })}
            <Button
                onClick={() => addGroup(schoolId)}
                variant="outlined"
                sx={addGroupButtonSx}
                disabled={!isInitialized || chosenGrades.length === 16 || isDoingAjax || saving}
            >
                Add new grade level group
            </Button>
        </Box>
    );
};

export default FeesTable;